import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
import {
	HERO_IMG_PLACEMENTS,
	HeroImagePlacements,
	Icon,
	TEXT_ALIGNMENT_OPTIONS,
	TextAlignmentOptions,
	BG_IMAGE_ALIGNMENT_OPTIONS,
	BgImageAlignmentOptions,
	Url,
} from '#const';
import { getIconUrl } from '#utils';

const Hero = dynamic(() => import('./Hero'));

export const defaultOptions = {
	heading: '<p><strong>ATTENTION <u>time is limited</u> to File!</strong></p>',
	headingAlignment: TextAlignmentOptions.LEFT,
	copy: '<p>Placeholder body text.</p>',
	copyAlignment: TextAlignmentOptions.LEFT,
	hideCopy: false,
	showList: false,
	smallBullets: false,
	centerBullets: false,
	listItems: [
		{ content: '<h3>List Item 1</h3><p>Description for list item 1</p>' },
		{ content: '<h3>List Item 2</h3><p>Description for list item 2</p>' },
	],
	lowerCopy: '<h3>Placeholder body text.</h3>',
	narrowWidth: true,
	textBox: false,
	mobileHeading: '<p><strong>ATTENTION <u>time is limited</u> to File!</strong></p>',
	mobileCopy: '<p>Placeholder body text.</p>',
	mobileCopyAlignment: TextAlignmentOptions.LEFT,
	mobileLowerCopy: '<h3>Placeholder body text.</h3>',
	fullWidth: false,
} as const;

Builder.registerComponent(Hero, {
	name: 'Hero',
	inputs: [
		{
			name: 'heading',
			friendlyName: 'Desktop Heading',
			type: 'richText',
			required: true,
			defaultValue: defaultOptions.heading,
		},
		{
			name: 'headingAlignment',
			friendlyName: 'Desktop Heading Alignment',
			type: 'string',
			enum: [...TEXT_ALIGNMENT_OPTIONS],
			defaultValue: defaultOptions.headingAlignment,
			helperText: 'Optional text alignment for the heading.',
		},
		{
			name: 'copy',
			friendlyName: 'Desktop Text',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.copy,
		},
		{
			name: 'copyAlignment',
			friendlyName: 'Desktop Text Alignment',
			type: 'string',
			enum: [...TEXT_ALIGNMENT_OPTIONS],
			defaultValue: defaultOptions.copyAlignment,
			helperText: 'Optional text alignment for the body text.',
		},
		{
			name: 'hideCopy',
			friendlyName: 'Hide Copy Section',
			type: 'boolean',
			defaultValue: defaultOptions.hideCopy,
			helperText: 'Enable to hide the copy and foreground image section',
		},
		{
			name: 'showList',
			friendlyName: 'Show List Section',
			type: 'boolean',
			defaultValue: defaultOptions.showList,
			helperText: 'Enable to add a list section above the lower copy',
		},
		{
			name: 'smallBullets',
			friendlyName: 'Small Bullets',
			type: 'boolean',
			showIf: function (options) {
				return options.get('showList') === true;
			},
			defaultValue: defaultOptions.smallBullets,
			helperText: 'Enable to use smaller bullets for the list',
		},
		{
			name: 'centerBullets',
			friendlyName: 'Center Bullets',
			type: 'boolean',
			showIf: function (options) {
				return options.get('showList') === true;
			},
			defaultValue: defaultOptions.centerBullets,
			helperText: 'Enable to verticallycenter the bullets',
		},
		{
			name: 'listItems',
			type: 'list',
			showIf: function (options) {
				return options.get('showList') === true;
			},
			subFields: [
				{
					name: 'icon',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
					required: false,
				},
				{
					name: 'content',
					friendlyName: 'Content',
					type: 'richText',
					required: true,
					defaultValue: '<h3>List Item</h3><p>Description for list item</p>',
				},
			],
			defaultValue: [...defaultOptions.listItems],
		},
		{
			name: 'foregroundImage',
			friendlyName: 'Desktop Foreground Image Information',
			type: 'object',
			required: false,
			helperText: 'Optional image placed next to text.',
			defaultValue: {
				src: Url.PLACEHOLDER_IMAGE,
				alt: '',
			},
			subFields: [
				{
					name: 'src',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'alt',
					friendlyName: 'Alt Text',
					type: 'string',
					helperText: 'Briefly describe the image for accessibility.',
				},
				{
					name: 'imgSide',
					friendlyName: 'Image Placement',
					type: 'string',
					enum: [...HERO_IMG_PLACEMENTS],
					defaultValue: HeroImagePlacements.RIGHT,
					helperText: 'Which side of the text should the foreground image appear?',
				},
			],
		},
		{
			name: 'lowerCopy',
			friendlyName: 'Desktop Lower Text',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.lowerCopy,
		},
		{
			name: 'backgroundImage',
			friendlyName: 'Desktop Background Image Information',
			type: 'object',
			required: false,
			helperText: 'Optional image placed in the background.',
			subFields: [
				{
					name: 'src',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'backgroundImagePlacement',
					friendlyName: 'Background Image Placement Bias',
					type: 'string',
					enum: [...BG_IMAGE_ALIGNMENT_OPTIONS],
					defaultValue: BgImageAlignmentOptions.CENTER,
					helperText: `How the background Image will attach to the background of the container.
						Use it to move the focal point of the image if needed.`,
				},
				{
					name: 'backgroundOpacity',
					friendlyName: 'Background Opacity',
					type: 'number',
					defaultValue: 1,
					min: 0,
					max: 1,
					step: 0.05,
					helperText: 'Opacity of the background image, from 0 to 1.',
				},
			],
		},
		{
			name: 'narrowWidth',
			friendlyName: 'Narrow Text Width',
			type: 'boolean',
			defaultValue: defaultOptions.narrowWidth,
			helperText: `Choose either false for default or true for narrow text against the form?
			Do NOT use with foreground image, it will not lay out correctly.`,
		},
		{
			name: 'textBox',
			friendlyName: 'Box around Text',
			type: 'boolean',
			defaultValue: defaultOptions.textBox,
			helperText: `Select to give text a transparent background, to make text more legible 
			over background images. Do NOT use with foreground image, it will not lay out correctly.`,
		},
		{
			name: 'mobileHeading',
			friendlyName: 'Mobile Heading',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.mobileHeading,
		},
		{
			name: 'mobileCopy',
			friendlyName: 'Mobile Text',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.mobileCopy,
		},
		{
			name: 'mobileCopyAlignment',
			friendlyName: 'Mobile Text Alignment',
			type: 'string',
			enum: [...TEXT_ALIGNMENT_OPTIONS],
			defaultValue: defaultOptions.mobileCopyAlignment,
			helperText: 'Optional text alignment for the body text.',
		},
		{
			name: 'mobileForegroundImage',
			friendlyName: 'Mobile Foreground Image Information',
			type: 'object',
			required: false,
			helperText: 'Optional image placed next to text.',
			defaultValue: {
				src: Url.PLACEHOLDER_IMAGE,
				alt: '',
			},
			subFields: [
				{
					name: 'src',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'alt',
					friendlyName: 'Alt Text',
					type: 'string',
					helperText: 'Briefly describe the image for accessibility.',
				},
				{
					name: 'imgSide',
					friendlyName: 'Image Placement',
					type: 'string',
					enum: [...HERO_IMG_PLACEMENTS],
					defaultValue: HeroImagePlacements.RIGHT,
					helperText: 'Which side of the text should the foreground image appear?',
				},
			],
		},
		{
			name: 'mobileLowerCopy',
			friendlyName: 'Mobile Lower Text',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.mobileLowerCopy,
		},
		{
			name: 'mobileBackgroundImage',
			friendlyName: 'Mobile Background Image',
			type: 'object',
			required: false,
			helperText: 'Optional image placed in the background.',
			subFields: [
				{
					name: 'src',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'backgroundImagePlacement',
					friendlyName: 'Background Image Placement Bias',
					type: 'string',
					enum: [...BG_IMAGE_ALIGNMENT_OPTIONS],
					defaultValue: BgImageAlignmentOptions.CENTER,
					helperText: `How the background Image will attach to the background of the container.
						Use it to move the focal point of the image if needed.`,
				},
				{
					name: 'backgroundOpacity',
					friendlyName: 'Mobile Background Opacity',
					type: 'number',
					defaultValue: 1,
					min: 0,
					max: 1,
					step: 0.05,
					helperText: 'Opacity of the background image on mobile devices, from 0 to 1.',
				},
			],
		},
		{
			name: 'fullWidth',
			friendlyName: 'Full Width',
			type: 'boolean',
			defaultValue: defaultOptions.fullWidth,
			helperText: 'If true, content will span full width instead of 8 columns',
		},
	],
	image: getIconUrl(Icon.HERO),
});
